///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { GtthRefreshTopologyCommand } from '../project/refresh-topology-command';
import { ActivityDTO } from '../../../../../services/project-socket/activity-dto.model';
import { GtthHistoryService } from '../../history-service';
import { GtthLockProjectCommand } from '../project/lock-project-command';
import { GtthUnlockProjectCommand } from '../project/unlock-project-command';
import { GtthCanRunDesignCommand } from './can-run-design-command';
import { GtthProgressWindowModel } from '../../../../project-windows/progress-window/progress-window.model';
import { GtthLogicalTopology } from '../../../entities/logical-topology';
import { GtthLayerDtoModel } from '../../../../../services/project-socket/dto/gtth-layer-dto.model';
import { AutoSetFrequenciesDTO, CostFunctionEnum, DesignRingsDTO, TopologyValidityDTO } from '../../../../../generated/shared-types.model';
import { firstValueFrom } from 'rxjs';

export class GtthAutoDesignCommand extends GtthAbstractCommand {
    static createDesignRindsDTO(data?: DesignRingsDTO): DesignRingsDTO {
        return {
            rand_seed: data?.rand_seed ?? 1,
            lpn_func: data?.lpn_func ?? CostFunctionEnum.POLYNOMIAL,
            lpn_fac: data?.lpn_fac ?? 1.0,
            lpn_th: data?.lpn_th ?? 5,
            lpn_weig: data?.lpn_weig ?? 1.0,
            noh_func: data?.noh_func ?? CostFunctionEnum.POLYNOMIAL,
            noh_fac: data?.noh_fac ?? 1.0,
            noh_th: data?.noh_th ?? 16,
            noh_weig: data?.noh_weig ?? 1.0,
            use_dlim: data?.use_dlim ?? false,
            dist_lim: data?.dist_lim ?? 2500,
            rsz_th: data?.rsz_th ?? 0,
            rsz_weig: data?.rsz_weig ?? 0.0,
            opn_weig: data?.opn_weig ?? 1.0,
            bottom_up: data?.bottom_up ?? true,
            supp_layer: data?.supp_layer ?? GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX
        } as DesignRingsDTO;
    }

    static createAutoSetFrequenciesDTO(data?: AutoSetFrequenciesDTO): AutoSetFrequenciesDTO {
        const wc_analysis = data?.wc_analysis ?? false;
        const explicit_only = data?.explicit_only ?? !wc_analysis;
        const pol_priority = data?.pol_priority ?? true;
        const pos_accuracy = data?.pos_accuracy ?? 5.0;
        const rand_seed = data?.rand_seed ?? 1;
        const force_new = data?.force_new ?? false;

        return {
            wc_analysis,
            explicit_only,
            pol_priority,
            pos_accuracy,
            rand_seed,
            force_new
        } as AutoSetFrequenciesDTO;
    }

    static checkNodesConnectivity(historyService: GtthHistoryService): Promise<boolean> {
        return firstValueFrom(historyService.reportsService.validateTopologyBeforeDesign()).then(
            (topologyValidityDto: TopologyValidityDTO) => {
                const hasUnconnectedSites = topologyValidityDto?.connectedSitesReport?.some(
                    (value) => value.enabled && !value.buOnly && !value.isConn
                );

                const hasProblematicDevices = topologyValidityDto.problematicBuDevices?.length > 0;

                if (hasUnconnectedSites || hasProblematicDevices) {
                    historyService.reportsService.setTopologyValidityToCache(topologyValidityDto);
                    historyService.progressWindowService.handleDesignError({
                        message: 'Some nodes cannot be connected to POP. No optimization performed',
                        topologyValidity: topologyValidityDto
                    });
                    return false;
                } else {
                    return true;
                }
            }
        );
    }

    static calculateSupportLayersMask(historyService: GtthHistoryService, layerIndex: number) {
        const enabledNodesPerLayer = historyService.logicalTopology.getNodesPerLayerCount(false);

        let supportMask = 0;
        for (let index = 0; index < enabledNodesPerLayer.length; index++) {
            if (index === layerIndex || enabledNodesPerLayer[index] > 0) {
                supportMask = supportMask | Math.pow(2, index);
            }
        }

        historyService.logicalTopology.autodesignP2mpPerLayer[layerIndex].feed_by = supportMask;
        historyService.logicalTopology.designRingsPerLayer[layerIndex].supp_layer = supportMask;
    }

    static isPopInTopologyExist(historyService: GtthHistoryService): boolean {
        if (!historyService.logicalTopology.hasPopStationsInTopmostLayer()) {
            if (historyService.logicalTopology.layersModeEnabled) {
                historyService.notificationService.showError(
                    'No POP found in topmost layer, or POP not connected. No optimization performed'
                );
            } else {
                historyService.notificationService.showError('No POP found, or POP not connected. No optimization performed');
            }
            return false;
        }

        return true;
    }

    getCommandName(): string {
        return 'GtthAutoDesignCommand';
    }

    create(): this {
        return this;
    }

    doCommand(): void {
        if (!GtthAutoDesignCommand.isPopInTopologyExist(this.historyService)) {
            this.commandResolve(false);
            return;
        }

        this.historyService.notificationService.clearAllMessages();

        this.historyService.canCommandLockProject = true;

        this.historyService
            .doCommandBypassHistory(this.historyService.command(GtthCanRunDesignCommand))
            .then((canRunDesign: boolean) => {
                if (!canRunDesign) {
                    return Promise.reject();
                }

                this.historyService.progressWindowService.show(GtthProgressWindowModel.create(this.getCommandName()));
                return GtthAutoDesignCommand.checkNodesConnectivity(this.historyService);
            })
            .then((nodesAreConnected: boolean) => {
                if (!nodesAreConnected) {
                    return Promise.reject();
                }

                this.historyService.bomReportService.clearBom();
                this.historyService.logicalTopology.clearNodeNamesSectorsToShow();
                return this.historyService.logicalTopology.setShowPlanningMode(this.historyService, false, true, true);
            })
            .then((proceedExecution: boolean) => {
                if (!proceedExecution) {
                    return Promise.reject();
                }

                this.prepareLayers();

                const unlockCommand = this.historyService.command(GtthUnlockProjectCommand);
                return this.historyService.doCommandBypassHistory(unlockCommand, true);
            })
            .then((proceedExecution: boolean) => {
                if (!proceedExecution) {
                    return Promise.reject();
                }

                const activity = this.createActivityDTO(false);
                activity.logicalTopology = this.historyService.logicalTopology.toGeoJsonMainConfig(this.historyService, this.project.name);
                activity.designConfig = this.historyService.logicalTopology.getDesignConfig();
                this.sendActivity(activity);
            })
            .catch((error) => {
                console.error(error);
                this.historyService.progressWindowService.hide();
                this.commandResolve(false);
            });
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        const progressDto = GtthProgressWindowModel.createFromActivityDTO(activityDto);

        this.historyService.progressWindowService.show(progressDto);

        if (progressDto.isCommandFinished()) {
            if (this.historyService.canCommandLockProject) {
                const lockTimeout = this.commandResolve ? 0 : 1000;
                if (this.commandResolve) {
                    this.commandResolve(progressDto.isSuccessfull());
                }

                setTimeout(() => {
                    const lockCommand = this.historyService.command(GtthLockProjectCommand).create(false);
                    this.historyService.doCommandBypassHistory(lockCommand).then(() => {
                        this.historyService.clearHistory();
                    });
                }, lockTimeout);
            }

            this.refreshTopology(progressDto);
        }
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    private prepareLayers(): void {
        const layers: GtthLayerDtoModel[] = this.historyService.logicalTopology.layers;

        if (layers?.length > 0) {
            layers.forEach((_value: GtthLayerDtoModel, index: number) => {
                GtthAutoDesignCommand.calculateSupportLayersMask(this.historyService, index);

                if (index > 0) {
                    const isBottomUpDesign =
                        this.historyService.logicalTopology.autodesignP2mpPerLayer[GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX]
                            .bottom_up;

                    this.historyService.logicalTopology.autodesignP2mpPerLayer[index].bottom_up = isBottomUpDesign;
                    this.historyService.logicalTopology.designRingsPerLayer[index].bottom_up = isBottomUpDesign;
                }
            });
        } else {
            GtthAutoDesignCommand.calculateSupportLayersMask(this.historyService, GtthLogicalTopology.DEFAULT_CONFIG_LAYER_INDEX);
        }
    }

    private refreshTopology(progressDto: GtthProgressWindowModel) {
        const refreshTopologyCommand = this.historyService.command(GtthRefreshTopologyCommand).create(false);
        this.historyService.doCommandBypassHistory(refreshTopologyCommand).then(() => {
            this.historyService.selectWholeTopologyLayer();

            if (this.commandResolve) {
                this.commandResolve(progressDto.isSuccessfull());
            }
        });
    }
}
